import { apiClient } from '../../lib/api/client'
import type { SpecSelection } from '../../lib/rfq/rfq-bundle-context'
import { buildAppendLinesRequestFromReviewLines } from './review-domain'
import type { RfqReviewLine } from './review-domain'
import { classifyAppendConflict } from './rfq-append-lines'
import { isStaleDraftProblem } from './stale-draft-recovery'

/**
 * ADR-0117 D11 (2026-08-27) — THE standing, named reconcile seam.
 *
 * **The regression this exists to fix.** UID-346 turned the RFQ compose
 * Configure action from a purely local `rfqBundle.setDraft({specSelections})`
 * write into a server-backed `POST /rfq/{rfqId}/lines/bulk-configure`. But
 * the eager 0→1 draft-create effect (`RfqReviewPage.tsx`) only ever rides the
 * line(s) present at that instant — normally one — and the only full-draft
 * sync points are Share and Submit's `PATCH /rfq/{rfqId}`. A 2nd/3rd/... part
 * added locally therefore has no counterpart on the server draft, and
 * `bulk-configure`'s all-or-nothing set write fails outright. That is a
 * regression against `main` (where Configure worked for every line
 * regardless of server state) in the ticket's own headline N>1 flow (AC1).
 *
 * **The fix, and why it is THIS shape.** Append the missing lines via the
 * existing `POST /rfq/{rfqId}/lines` (`appendRfqLines`, ADR-0093 D2) —
 * NEVER a full-array `PATCH /rfq/{rfqId}`. `PATCH`'s `lines` is a documented
 * full replacement whose compare-and-set is on `status` ALONE, never a
 * version on `lines` (ADR-0093 H-1) — replaying a possibly-stale array
 * against a live draft is an unguarded lost update, exactly what code
 * review's W6 removed from this same call path. An append "removes that
 * class BY CONSTRUCTION: the client transmits only what it is adding"
 * (ADR-0093 D2) — restoring the PATCH here would silently reintroduce W6's
 * defect one call site later, which is why this function must never do it.
 *
 * **STANDING RULE (binding on this repo, ADR-0117 D11):** the client
 * `RfqBundle` remains the source of truth during compose (ADR-0082 D1,
 * unamended). Any server-backed compose-time operation MUST reconcile the
 * server draft's line set through THIS seam before operating on it, and
 * MUST NOT full-array `PATCH /rfq/{rfqId}` to do it. A compose-time operation
 * that assumes the server draft already contains a locally-added line is a
 * bug — this is the SECOND time that assumption has shipped (the first was
 * this very regression), so the fix is a NAMED, REUSABLE function precisely
 * so a third server-backed compose-time operation (e.g. the deferred
 * INT-CONFIG-03 per-part override) calls this instead of re-deriving it.
 * Do not inline this logic at a call site again.
 *
 * **Lazy timing is the DURABLE end state, not a narrower stand-in for an
 * eager one.** D11 rejects "eager-at-line-add" as the real end state: it
 * still needs this exact reconcile-before-operate step on any failure (so it
 * saves nothing), it would amend ADR-0082 D1's deliberate "no per-keystroke
 * autosave" choice (which UID-346 does not own), and continuous server sync
 * is a strictly stronger guarantee than anything a compose-time operation
 * ever actually needs. Calling this seam at the moment of use is self-healing
 * by construction — it cannot be stale, because it runs when staleness would
 * matter.
 *
 * **The 409 case — reconcile, never swallow.** A `line-already-present` 409
 * means the desired postcondition already holds (the flow MAY proceed), but
 * it ALSO means this function's caller's picture of the server's line set was
 * WRONG at the exact moment it was about to make an all-or-nothing write —
 * the precise kind of thing ADR-0093 D4's "not a silent merge" / R4's "make
 * the loss loud" forbids hiding. A 409 body is a `Problem`, not the `RfqView`
 * a 200 returns, so this function RE-READS `GET /rfq/{rfqId}` before
 * proceeding rather than guessing that "already present" means "everything I
 * wanted is now there" — an unrelated line could be the one that collided.
 * Only lines the re-read actually confirms are treated as ensured; anything
 * still missing after the re-read is a real, reported failure, not a retry
 * loop and not a silent partial success.
 *
 * **No id-remapping hazard.** The append and `bulk-configure` operate on the
 * SAME id space — `bulk-configure` resolves `lineIds` against sourceable ids
 * (`apps/api/.../rfq/service.ts`'s `linesBySourceable`), and the compose
 * line's local `id` IS the sourceable id (`review-domain.ts`'s `toReviewLine`)
 * — so there is no server-minted line id this function needs to translate
 * back to the caller. It deals purely in existence, never identity.
 */
export type EnsureLinesOnServerDraftResult =
  | {
      readonly ok: true
      readonly serverLineIds: ReadonlySet<string>
      /**
       * UID-530 AC3 — the per-line configurations the `RfqView` this call saw
       * was carrying (see {@link specSelectionsOf}). `undefined` on the no-op
       * branch, where no network call happened and therefore no view was seen
       * — deliberately distinct from `{}` ("a view was seen and it carried
       * nothing"), so a caller can tell the two apart.
       *
       * Carried HERE rather than re-derived by the caller because this
       * function is the only thing that holds those views: the eager 0→1
       * create effect rides only the lines present at that instant, so a
       * 2nd/3rd part's server snapshot first reaches the client on this
       * seam's append response.
       */
      readonly specSelections?: Readonly<Record<string, SpecSelection>>
    }
  | {
      readonly ok: false
      readonly message: string
      /**
       * Code review C3 (2026-08-27) — whatever this call DID learn about the
       * server's actual line set before failing (e.g. a 409's reconcile
       * `GET`), so the caller can persist it even on failure. Without this,
       * a caller that only calls `setServerLineIds` on the `ok:true` branch
       * discards a round trip's worth of knowledge and repeats the identical
       * failing call forever. `undefined` when nothing new was learned (the
       * very first append attempt failed outright, never reaching a GET).
       */
      readonly serverLineIds?: ReadonlySet<string>
      /**
       * True when the failure was a 404 on a `{rfqId}`-scoped call — i.e. the
       * caller's `rfqId` no longer names a draft at all (a stale, persisted
       * `draftRfqId`), NOT anything about the lines being appended. See
       * `stale-draft-recovery.ts` for why that has exactly one honest
       * interpretation and what the caller does with it.
       *
       * REPORTED, never recovered here: this seam deals in a draft's line set,
       * not in draft IDENTITY. Minting a replacement draft from inside it would
       * put two owners on `rfqBundle.draftRfqId` and hide the swap from the very
       * call sites that then have to operate on the new id.
       */
      readonly staleDraft?: boolean
      /** UID-530 AC3 — the C3 rule applied to the configuration harvest too:
       *  a failure that still reached a view hands back what that view carried,
       *  so the caller is never worse off than before the call. */
      readonly specSelections?: Readonly<Record<string, SpecSelection>>
    }

/** Any RfqView-shaped response (create/append/bulk-configure/GET all return
 * the full, current line set) reduced to the sourceable ids it carries. */
export function serverLineIdsOf(view: {
  readonly lines?: readonly { readonly sourceable: { readonly id: string } }[]
}): ReadonlySet<string> {
  return new Set((view.lines ?? []).map((line) => line.sourceable.id))
}

/**
 * UID-530 AC3 — the same view, reduced to the per-line CONFIGURATIONS it
 * carries, keyed by sourceable id. The exact sibling of
 * {@link serverLineIdsOf}, and ONE reducer for the same reason: it is read by
 * every harvest site (the eager create, this seam's three view-bearing return
 * points, `bulk-configure`, the shipping PATCH-sync), and a second copy could
 * disagree about which lines count as configured.
 *
 * WHY A HARVEST EXISTS AT ALL. The server SNAPSHOTS a library part's saved
 * configuration onto the line at write time (UID-530, the sibling of ADR-0063's
 * `requirements` and ADR-0138's part identity). So a response can carry a
 * configuration the client never authored — and the compose screen's Configure
 * gate reads `draft.specSelections`, which nothing but the Configure modal used
 * to write. Without this, a part the buyer had already configured in the Library
 * still reads "Needs configuration" and still blocks submit.
 *
 * TWO DELIBERATE SKIPS, both of which would otherwise clear the gate on a line
 * that is genuinely unconfigured:
 *  - a `null`/absent `specSelection` (a catalog line, or a library part that is
 *    not fully configured — the honest "no snapshot" the server writes), and
 *  - a selection with no `specification` (a legacy bag-only row). The gate reads
 *    `specSelection?.specification`, so an entry without one is worse than no
 *    entry: it says "configured" and has nothing to show.
 */
export function specSelectionsOf(view: {
  readonly lines?: readonly {
    readonly sourceable: { readonly id: string }
    readonly specSelection?: SpecSelection | null
  }[]
}): Readonly<Record<string, SpecSelection>> {
  // A NULL-PROTOTYPE accumulator (LOW-4): this map is keyed by a sourceable id
  // we do not mint, and a plain `{}` inherits `toString`/`constructor`/… which
  // any downstream `in`/property read would then see as present entries.
  const harvested: Record<string, SpecSelection> = Object.create(null)
  for (const line of view.lines ?? []) {
    const selection = line.specSelection
    if (selection?.specification) {
      harvested[line.sourceable.id] = selection
    }
  }
  return harvested
}

const GENERIC_FAILURE_MESSAGE = 'Could not save this configuration. Please try again.'

/**
 * Ensures every id in `targetLineIds` exists as a line on the server draft
 * `rfqId`, appending whichever ones `knownServerLineIds` says are missing
 * (looked up from `allLocalLines`), and returns the reconciled set of ids now
 * known to be on the server. Callers persist the returned `serverLineIds` (a
 * full replacement, not a union — see doc comment) so the next call starts
 * from an up-to-date picture.
 *
 * A no-op — no network call at all — when every target id is already in
 * `knownServerLineIds`, which is the common case once the first Configure on
 * a given draft has run.
 */
export async function ensureLinesOnServerDraft(
  rfqId: string,
  targetLineIds: readonly string[],
  allLocalLines: readonly RfqReviewLine[],
  knownServerLineIds: ReadonlySet<string>,
): Promise<EnsureLinesOnServerDraftResult> {
  const missingLineIds = targetLineIds.filter((id) => !knownServerLineIds.has(id))
  if (missingLineIds.length === 0) {
    return { ok: true, serverLineIds: knownServerLineIds }
  }

  const linesToAppend = allLocalLines.filter((line) => missingLineIds.includes(line.id))
  const appended = await apiClient.POST('/rfq/{rfqId}/lines', {
    params: { path: { rfqId } },
    body: buildAppendLinesRequestFromReviewLines(linesToAppend),
  })
  if (appended.data) {
    return {
      ok: true,
      serverLineIds: serverLineIdsOf(appended.data),
      specSelections: specSelectionsOf(appended.data),
    }
  }

  const status = (appended.error as { readonly status?: number } | undefined)?.status
  const conflict = status === 409 ? classifyAppendConflict(appended.error?.detail) : 'unknown'
  if (conflict !== 'line-already-present') {
    return {
      ok: false,
      message: appended.error?.detail ?? GENERIC_FAILURE_MESSAGE,
      // A 404 here is about `rfqId`, not about these lines — see `staleDraft`'s
      // doc comment on the result type.
      ...(isStaleDraftProblem(appended.error) ? { staleDraft: true } : {}),
    }
  }

  // D11 — a 409 here means the desired postcondition MAY already hold, but a
  // `Problem` carries no line set, so this function's picture of the server
  // draft just became stale at exactly the moment it's about to green-light
  // an all-or-nothing write. Re-read rather than assume.
  const refetched = await apiClient.GET('/rfq/{rfqId}', { params: { path: { rfqId } } })
  if (!refetched.data) {
    return {
      ok: false,
      message:
        refetched.error?.detail ??
        "Could not confirm this request's current parts. Please try again.",
      // The draft can vanish BETWEEN the append and this reconcile read (or the
      // 409 came from a draft that was already gone) — same signal, same reason.
      ...(isStaleDraftProblem(refetched.error) ? { staleDraft: true } : {}),
    }
  }
  const reconciled = serverLineIdsOf(refetched.data)
  const reconciledSpecs = specSelectionsOf(refetched.data)
  const stillMissingIds = targetLineIds.filter((id) => !reconciled.has(id))
  if (stillMissingIds.length === 0) {
    return { ok: true, serverLineIds: reconciled, specSelections: reconciledSpecs }
  }

  // Code review C3 (2026-08-27) — the 409 was real but wasn't (only) about
  // the lines this caller wanted: after reconciling against the server's
  // ACTUAL current set, at least one target is still genuinely absent. This
  // is NOT the same situation the first append attempt saw — this function
  // now knows EXACTLY which ids are still missing (not a stale, possibly-
  // wrong local guess) — so it is safe and correct to append exactly that
  // set, ONCE, rather than giving up. This is a single reconcile-then-
  // complete step, not a retry loop: if THIS append also fails, this
  // function reports failure and stops (no second re-read, no bounded-retry
  // machinery) — but it still hands back `reconciled`, the server line set
  // this call DID confirm, so the caller is never worse off than before this
  // call for the ids that genuinely were resolved.
  const stillMissingLines = allLocalLines.filter((line) => stillMissingIds.includes(line.id))
  const secondAppend = await apiClient.POST('/rfq/{rfqId}/lines', {
    params: { path: { rfqId } },
    body: buildAppendLinesRequestFromReviewLines(stillMissingLines),
  })
  if (secondAppend.data) {
    return {
      ok: true,
      serverLineIds: serverLineIdsOf(secondAppend.data),
      specSelections: specSelectionsOf(secondAppend.data),
    }
  }
  return {
    ok: false,
    message: isStaleDraftProblem(secondAppend.error)
      ? (secondAppend.error?.detail ?? GENERIC_FAILURE_MESSAGE)
      : 'Could not save this configuration — one or more parts are not yet on this request.',
    serverLineIds: reconciled,
    specSelections: reconciledSpecs,
    // Reachable: the draft can be abandoned/reaped between the reconcile GET
    // above and this append. Reporting it lets the caller recover instead of
    // blaming the buyer's parts for a draft that no longer exists.
    ...(isStaleDraftProblem(secondAppend.error) ? { staleDraft: true } : {}),
  }
}
